import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Split CSV text into logical rows. Newlines inside quoted fields are kept as part of the field.
 */
function splitCSVRows(text) {
  const rows = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (c === '"') {
      inQuotes = !inQuotes;
      current += c;
    } else if (c === '\n' || c === '\r') {
      if (inQuotes) {
        current += c;
      } else {
        if (c === '\r' && text[i + 1] === '\n') i += 1;
        if (current.trim().length > 0) rows.push(current);
        current = '';
      }
    } else {
      current += c;
    }
  }
  if (current.trim().length > 0) rows.push(current);
  return rows;
}

/**
 * Parse a single CSV line respecting quoted fields (handles commas inside quotes).
 */
function parseCSVLine(line) {
  const result = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      i += 1;
      let field = '';
      while (i < line.length) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') {
            field += '"';
            i += 2;
          } else {
            i += 1;
            break;
          }
        } else {
          field += line[i];
          i += 1;
        }
      }
      result.push(field);
      if (line[i] === ',') i += 1;
    } else {
      let field = '';
      while (i < line.length && line[i] !== ',') {
        field += line[i];
        i += 1;
      }
      result.push(field.trim());
      if (line[i] === ',') i += 1;
    }
  }
  return result;
}

/**
 * Parse CSV text into rows of columns. First row is treated as header.
 * Handles multiline quoted fields (newlines inside quotes do not start a new row).
 */
function parseCSV(text) {
  const logicalLines = splitCSVRows(text);
  if (logicalLines.length === 0) return { header: [], rows: [] };
  const header = parseCSVLine(logicalLines[0]);
  const rows = logicalLines.slice(1).map((line) => parseCSVLine(line));
  return { header, rows };
}

/* eslint-disable import/prefer-default-export */
export const useGeneralProvidersStore = defineStore('generalProviders', () => {
  const providers = ref([]);
  const loading = ref(false);

  async function fetchProviders() {
    loading.value = true;
    try {
      const res = await fetch('/ProviderGI.csv');
      if (!res.ok) throw new Error(`Failed to load CSV: ${res.status}`);
      const text = await res.text();
      const { header, rows } = parseCSV(text);

      const nameCol = header.findIndex((h) => /^SPR_NAME$/i.test(String(h).trim()));
      const locationCol = header.findIndex((h) => /^SPR_PHYSICAL_ADDRESS$/i.test(String(h).trim()));
      const typeCol = header.findIndex((h) => /^SPT_NAME$/i.test(String(h).trim()));
      const phoneCol = header.findIndex((h) => /^PHONE$/i.test(String(h).trim()));

      const ni = nameCol >= 0 ? nameCol : 0;
      const li = locationCol >= 0 ? locationCol : 1;
      const ti = typeCol >= 0 ? typeCol : 2;
      const pi = phoneCol >= 0 ? phoneCol : 3;

      const norm = (s) => (s ?? '').trim().replace(/\r?\n/g, ' ') || undefined;
      providers.value = rows.map((row, index) => ({
        id: `row-${index + 1}`,
        name: norm(row[ni]),
        location: norm(row[li]),
        providerType: norm(row[ti]),
        phone: norm(row[pi]),
      }));
    } catch (e) {
      // console.error('Error loading general providers CSV:', e);
      providers.value = [];
    } finally {
      loading.value = false;
    }
  }

  return { providers, loading, fetchProviders };
});
